import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider, ThemeContext } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import { BodyMedium500, BodyLarge500 } from '@nebula/global-styles';
import { HorizontalProgressBar } from '@nebula/progress-bar';
import { InfoIcon } from '@nebula/icons/ui';
import { useMediaQuery } from '@nebula/hooks';
import { InformationAlert } from '@nebula/alert';
import { useModal } from '@nebula/modal';
import dayjs from 'dayjs';
import {
  ChangeBillingDateStage,
  changeBillingDateTransition,
  isFlutterApp,
  MobileLocations,
  navigateTo,
} from '@ecs-uk/web-common-mobile-view-helpers';
import MonthlySubscriptionDayPicker from '../../../components/MonthlySubscriptionDayPicker';
import { calculateDaysBetween } from '../../../helpers/dateCalculations';
import CancelBillingDateChangeDialog from '../../../components/CancelBillingDateChangeDialog';
import selectBillDate from '../../../actions/billing/selectBillingDate';
import usePageView from '../../../hooks/usePageView';
import {
  publishClickedOnContinueForChangeBillingDate,
  publishCancelClick,
} from '../../../helpers/changeBillingDate/analytics';
import {
  ChooseNewBillingDateWrapper,
  ChooseNewBillingDateContainer,
  StepIndicatorContainer,
  ChooseNewBillingDateTitleMedium500,
  ChooseNewBillingDateTitleLarge500,
  ChooseNewBillingDateTextLinkBack,
  ChooseNewBillingDateTitleContainer,
  DateNotSelectedErrorPlaceholder,
  DateNotSelectedErrorMessage,
  ChooseNewBillingDateFooter,
  InformationAlertContainer,
  CancelAndContinueContainer,
  ContinueOrCancelButton,
  AriaHideContainer,
} from './index.styles';
import { PAYMENT_STAGES, PaymentContext } from '../../../components/PaymentOrchestrator/PaymentContextProvider';

const PAGE_NAME = 'ProdMoveChooseNewBillingDatePage';

const ChooseNewBillingDate = () => {
  const theme = useContext(ThemeContext);
  const isDesktop = useMediaQuery(theme.media.tabletPortraitMin);
  const eligibility = useSelector(state => state?.billing?.dateChangeEligibility.data);
  const selectedDateFromState = useSelector(state => state.billingDateSelection?.selectedBillDate);

  const history = useHistory();
  const dispatch = useDispatch();

  const { isWebview, goToPaymentStage } = useContext(PaymentContext);

  const [selectedDate, setSelectedDate] = useState(selectedDateFromState);
  const [displayError, setDisplayError] = useState(false);
  const [errorKey, setErrorKey] = useState(0);
  const errorRef = useRef(null);

  const { isShowing, toggle } = useModal();

  const isTabletLandscapeMin = useMediaQuery(nebulaTheme.media.tabletLandscapeMin);

  usePageView(PAGE_NAME);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [eligibility?.earliestSelectableDate, isWebview, history]);

  useEffect(() => {
    if (isWebview) {
      changeBillingDateTransition({
        stage: selectedDate ? ChangeBillingDateStage.DATE_SELECTED : ChangeBillingDateStage.DATE_NOT_SELECTED,
      });
    }
  }, [selectedDate, isWebview]);

  const handleBackOrCancel = useCallback(
    e => {
      e.preventDefault();

      if (!selectedDate) {
        publishCancelClick('choose_new_billing_date_page');
        history.goBack();
        return;
      }

      if (isWebview && isFlutterApp()) {
        navigateTo(MobileLocations.CHANGE_BILLING_DATE_CANCEL_MODAL);
        return;
      }

      toggle();
    },
    [selectedDate, history, toggle, isWebview]
  );
  useEffect(() => {
    if (!isWebview) {
      // Delay focusing slightly to ensure the [data-back-link="true"] element
      // has been rendered and attached to the DOM before querying and focusing it.
      const timer = setTimeout(() => {
        const backLinkElement = document.querySelector('[data-back-link="true"]');
        if (backLinkElement) {
          backLinkElement.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isWebview]);

  useEffect(() => {
    if (displayError && !selectedDate && errorRef.current) {
      errorRef.current.focus();
    }
  }, [displayError, selectedDate, errorKey]);

  const fromDate = new Date(eligibility.earliestSelectableDate);
  fromDate.setDate(fromDate.getDate() - 1);
  const toDate = new Date(eligibility.latestSelectableDate);

  const daysBetween = calculateDaysBetween(fromDate, selectedDate);

  const handleContinue = () => {
    if (!selectedDate) {
      setDisplayError(true);
      setErrorKey(prev => prev + 1);
      return;
    }

    setDisplayError(false);
    dispatch(selectBillDate(selectedDate, daysBetween));

    const formattedSelectedDate = dayjs(selectedDate).format('MM/DD/YYYY');
    publishClickedOnContinueForChangeBillingDate(formattedSelectedDate);
    goToPaymentStage(PAYMENT_STAGES.REVIEW_AND_PAY);
  };

  const [ChooseNewBillingDateTitle, StepIndicatorTitle] = isTabletLandscapeMin
    ? [ChooseNewBillingDateTitleLarge500, BodyLarge500]
    : [ChooseNewBillingDateTitleMedium500, BodyMedium500];

  return (
    <ThemeProvider theme={nebulaTheme}>
      <ChooseNewBillingDateWrapper id="choose-new-billing-date-wrapper">
        <ChooseNewBillingDateContainer>
          {!isWebview && (
            <ChooseNewBillingDateTextLinkBack
              id="choose_date_back"
              data-back-link="true"
              href={selectedDate ? '#' : '/billing'}
              onClick={e => handleBackOrCancel(e)}
            >
              Back
            </ChooseNewBillingDateTextLinkBack>
          )}
          <StepIndicatorContainer id="step-indicator-container">
            <StepIndicatorTitle>Step 1 of 2</StepIndicatorTitle>
            <AriaHideContainer>
              <HorizontalProgressBar maxValue={100} value={50} color={nebulaTheme.color.messaging.green[50]} />
            </AriaHideContainer>
          </StepIndicatorContainer>
          <ChooseNewBillingDateTitleContainer>
            <ChooseNewBillingDateTitle as="h1">Choose a new billing date</ChooseNewBillingDateTitle>
            {displayError && !selectedDate ? (
              <DateNotSelectedErrorPlaceholder
                key={errorKey}
                ref={errorRef}
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                tabIndex={-1}
                aria-label="Error, Choose a new billing date"
              >
                <AriaHideContainer>
                  <InfoIcon iconSize="xSmall" iconColor={nebulaTheme.color.messaging.red[60]} aria-hidden="true" />
                  <DateNotSelectedErrorMessage>Choose a new date to continue</DateNotSelectedErrorMessage>
                </AriaHideContainer>
              </DateNotSelectedErrorPlaceholder>
            ) : null}
          </ChooseNewBillingDateTitleContainer>
          <MonthlySubscriptionDayPicker
            selectedDate={selectedDate}
            onSelect={setSelectedDate}
            fromDate={fromDate}
            toDate={toDate}
          />
          <ChooseNewBillingDateFooter>
            {selectedDate && (
              <InformationAlertContainer
                as="section"
                tabIndex={0}
                aria-label={`Next, we'll calculate a one-time payment This is not a fee. It will cover the ${daysBetween} ${
                  daysBetween === 1 ? 'day' : 'days'
                } between your current and chosen billing date.`}
              >
                <AriaHideContainer>
                  <InformationAlert
                    message={`This is not a fee. It will cover the ${daysBetween} ${
                      daysBetween === 1 ? 'day' : 'days'
                    } between your current and chosen billing date.`}
                    title="Next, we'll calculate a one-time payment"
                  />
                </AriaHideContainer>
              </InformationAlertContainer>
            )}
            <CancelAndContinueContainer>
              <ContinueOrCancelButton
                isWebview={isWebview}
                label="Continue"
                buttonKind="primaryMagenta"
                buttonSize={isDesktop ? 'large' : 'small'}
                type="button"
                onClick={handleContinue}
              />
              <ContinueOrCancelButton
                label="Cancel"
                buttonKind="secondaryMagenta"
                buttonSize={isDesktop ? 'large' : 'small'}
                type="button"
                onClick={e => handleBackOrCancel(e)}
              />
            </CancelAndContinueContainer>
          </ChooseNewBillingDateFooter>
        </ChooseNewBillingDateContainer>

        <CancelBillingDateChangeDialog
          isShowing={isShowing}
          toggleHide={toggle}
          onYes={() => {
            publishCancelClick('choose_new_billing_date_page');
            history.goBack();
          }}
        />
      </ChooseNewBillingDateWrapper>
    </ThemeProvider>
  );
};

export default ChooseNewBillingDate;
